/**
 * Textos da superfície interna, por idioma.
 *
 * Segue a clínica, não o delegado. Decisão de 2026-09-28: na esmagadora maioria
 * dos casos a assinatura acontece no tablet do delegado, pousado na mesa à
 * frente de quem assina — o ecrã é lido pela clínica.
 *
 * O idioma chega no `/contexto`, por isso até lá o ecrã está em branco a
 * carregar. Ao contrário da superfície pública, aqui não há ecrãs de erro
 * anteriores à resposta que precisem de pista no URL.
 */

import type { Idioma } from '@ac/shared';

interface Textos {
	titulo: string;
	aCarregar: string;
	distribuidores: string;
	procurar: string;
	semEmail: string;
	semCorrespondencia: string;
	semDistribuidores: string;
	nomeSignatario: string;
	nomeCompleto: string;
	aPreparar: string;
	assinarAgora: string;
	enviarParaAssinatura: string;
	confirme: string;
	signatario: string;
	rotuloDistribuidores: string;
	assinaturaClinica: string;
	aSelar: string;
	confirmarAssinatura: string;
	voltar: string;
	linkCriado: string;
	linkNota: (expira: string) => string;
	copiado: string;
	copiar: string;
	canalPorDefinir: string;
	assinado: string;
	seladas: (n: number) => string;
	envioBackoffice: string;
	erroInesperado: string;
}

const PT: Textos = {
	titulo: 'Autorização de consumos',
	aCarregar: 'A carregar…',
	distribuidores: 'Distribuidores a autorizar',
	procurar: 'Procurar distribuidor…',
	semEmail: 'sem email registado',
	semCorrespondencia: 'Nenhum distribuidor corresponde.',
	semDistribuidores: 'Nenhum distribuidor disponível.',
	nomeSignatario: 'Nome de quem assina',
	nomeCompleto: 'Nome completo',
	aPreparar: 'A preparar…',
	assinarAgora: 'Assinar agora',
	enviarParaAssinatura: 'Enviar para assinatura',
	confirme: 'Confirme antes de assinar',
	signatario: 'Signatário',
	rotuloDistribuidores: 'Distribuidores',
	assinaturaClinica: 'Assinatura da clínica',
	aSelar: 'A selar documentos…',
	confirmarAssinatura: 'Confirmar assinatura',
	voltar: 'Voltar',
	linkCriado: 'Link de assinatura criado',
	linkNota: (expira) =>
		`As autorizações ficaram por assinar, à espera da clínica. O link expira a ${expira} ` +
		'e só pode ser usado uma vez.',
	copiado: 'Copiado',
	copiar: 'Copiar',
	canalPorDefinir:
		'O canal de envio deste link ainda está por definir — por agora, copiar e enviar manualmente.',
	assinado: 'Assinado',
	seladas: (n) => `${n} autorizaç${n === 1 ? 'ão' : 'ões'} seladas e anexadas ao Bigin.`,
	envioBackoffice: 'O envio ao distribuidor é feito pelo backoffice, a partir do Bigin.',
	erroInesperado: 'Erro inesperado'
};

const ES: Textos = {
	titulo: 'Autorización de consumo',
	aCarregar: 'Cargando…',
	distribuidores: 'Distribuidores a autorizar',
	procurar: 'Buscar distribuidor…',
	semEmail: 'sin correo registrado',
	semCorrespondencia: 'Ningún distribuidor coincide.',
	semDistribuidores: 'No hay distribuidores disponibles.',
	nomeSignatario: 'Nombre de quien firma',
	nomeCompleto: 'Nombre completo',
	aPreparar: 'Preparando…',
	assinarAgora: 'Firmar ahora',
	enviarParaAssinatura: 'Enviar para firma',
	confirme: 'Confirme antes de firmar',
	signatario: 'Firmante',
	rotuloDistribuidores: 'Distribuidores',
	assinaturaClinica: 'Firma del cliente',
	aSelar: 'Sellando documentos…',
	confirmarAssinatura: 'Confirmar firma',
	voltar: 'Volver',
	linkCriado: 'Enlace de firma creado',
	linkNota: (expira) =>
		`Las autorizaciones quedaron pendientes de firma. El enlace caduca el ${expira} ` +
		'y solo puede usarse una vez.',
	copiado: 'Copiado',
	copiar: 'Copiar',
	canalPorDefinir:
		'El canal de envío de este enlace está aún por definir — por ahora, copiar y enviar manualmente.',
	assinado: 'Firmado',
	seladas: (n) => `${n} autorizaci${n === 1 ? 'ón' : 'ones'} selladas y adjuntadas a Bigin.`,
	envioBackoffice: 'El envío al distribuidor lo realiza el backoffice, desde Bigin.',
	erroInesperado: 'Error inesperado'
};

export function textos(idioma: Idioma): Textos {
	return idioma === 'es' ? ES : PT;
}
